import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Activity, CalendarCheck2, Users, Percent, XCircle, UserCog, TrendingUp, Loader2, AlertTriangle,
  ClipboardCheck, AlertOctagon, CalendarClock, ShieldCheck, CheckCircle2, ArrowRight } from "lucide-react";
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  PieChart, Pie, Cell,
} from "recharts";
import { api } from "../lib/api";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import PpDashboardView from "../components/PpDashboardView";

const TEAL = "#14b8a6";
const PALETTE = ["#14b8a6", "#0ea5e9", "#6366f1", "#f59e0b", "#ec4899", "#10b981", "#8b5cf6", "#ef4444"];
const PERIODS = ["week", "month", "quarter", "year", "custom"];

const PP_READ_ROLES = ["directeur", "directeur_adjoint", "directeur_vie_sociale", "coordinateur_vie_sociale", "infirmier_chef", "infirmier_chef_adjoint", "coordinateur_soins", "referent_incontinence",
  "kine", "logopede", "ergo", "animateur", "educateur", "educateur_specialise", "psychomotricien", "psychologue", "dieteticien", "referent_demence", "infirmier", "aide_soignant", "assistant_social", "medecin", "responsable_unite", "autre_pro"];


const Kpi = ({ icon: Icon, label, value, suffix, i, tid }) => (
  <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
    className="rounded-2xl bg-white/[0.04] ring-1 ring-white/10 p-5 backdrop-blur-sm" data-testid={`vs-kpi-${tid || label}`}>
    <div className="flex items-center gap-2 text-teal-300/80"><Icon className="h-4 w-4" /><span className="text-xs font-medium uppercase tracking-wide">{label}</span></div>
    <div className="mt-2 text-3xl font-extrabold text-white">{value}<span className="text-base font-semibold text-slate-400">{suffix}</span></div>
  </motion.div>
);

const Panel = ({ title, children, className = "" }) => (
  <div className={`rounded-2xl bg-white/[0.04] ring-1 ring-white/10 p-5 ${className}`}>
    <h3 className="text-sm font-semibold text-slate-200 mb-4">{title}</h3>
    {children}
  </div>
);

export default function VieSociale() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [view, setView] = useState("activities"); // activities | pp
  const [period, setPeriod] = useState("month");
  const [frm, setFrm] = useState("");
  const [to, setTo] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = { period };
      if (period === "custom" && frm && to) { params.frm = frm; params.to = to; }
      const { data } = await api.get("/planning/dashboard", { params });
      setData(data);
    } catch { setData(null); }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [period, frm, to]);

  const k = data?.kpis || {};
  const timeline = useMemo(() => (data?.timeline || []).map((t) => ({ ...t, jour: t.date?.slice(5) })), [data]);
  const activeCats = (data?.by_category || []).filter((c) => c.value > 0);
  const [showUnc, setShowUnc] = useState(false);
  const uncCount = (data?.by_category || []).find((c) => c.id === "")?.value || 0;
  const uncList = data?.unclassified_activities || [];

  return (
    <div className="min-h-screen bg-[#0b1620] text-slate-100 -m-4 sm:-m-6 p-4 sm:p-8" data-testid="vie-sociale-page">
      <div className="max-w-7xl mx-auto">
        {/* Bascule Activités / Projets Personnalisés */}
        {PP_READ_ROLES.includes(user?.role) && (
        <div className="inline-flex rounded-full bg-white/5 ring-1 ring-white/10 p-1 mb-6" data-testid="vs-view-toggle">
          {[["activities", Activity], ["pp", ClipboardCheck]].map(([v, Ic]) => (
            <button key={v} data-testid={`vs-view-${v}`} onClick={() => setView(v)}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition ${view === v ? "bg-teal-500 text-slate-900 shadow" : "text-slate-300 hover:text-white"}`}>
              <Ic className="h-4 w-4" />{v === "pp" ? t("pp.ppSection") : t("pp.activitiesSection")}
            </button>
          ))}
        </div>
        )}

        <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
          <div>
            <div className="flex items-center gap-2 text-teal-400"><Activity className="h-5 w-5" /><span className="text-xs font-semibold uppercase tracking-widest">{t("vieSociale.brand")}</span></div>
            <h1 className="text-3xl sm:text-4xl font-extrabold mt-1">{view === "pp" ? t("pp.ppSection") : t("vieSociale.title")}</h1>
            <p className="text-slate-400 text-sm mt-1">{view === "pp" ? t("pp.subtitle") : `${t("vieSociale.subtitle")}${data?.period ? ` · ${data.period.from} → ${data.period.to}` : ""}`}</p>
          </div>
          {view === "activities" && (
            <div className="flex flex-wrap items-center gap-2">
              {PERIODS.map((pk) => (
                <button key={pk} data-testid={`vs-period-${pk}`} onClick={() => setPeriod(pk)}
                  className={`px-3 py-2 rounded-full text-sm font-medium transition ${period === pk ? "bg-teal-500 text-white" : "bg-white/5 text-slate-300 hover:bg-white/10"}`}>{t(`vieSociale.period.${pk}`)}</button>
              ))}
            </div>
          )}
        </div>

        {view === "pp" ? (
          <PpDashboardView />
        ) : (
        <>
        {period === "custom" && (
          <div className="flex flex-wrap items-center gap-3 mb-6">
            <input type="date" value={frm} onChange={(e) => setFrm(e.target.value)} className="rounded-lg bg-white/5 ring-1 ring-white/10 px-3 py-2 text-sm" data-testid="vs-from" />
            <span className="text-slate-500">→</span>
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="rounded-lg bg-white/5 ring-1 ring-white/10 px-3 py-2 text-sm" data-testid="vs-to" />
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-32 text-slate-400"><Loader2 className="h-6 w-6 animate-spin mr-2" /> {t("vieSociale.loading")}</div>
        ) : !data ? (
          <div className="py-32 text-center text-slate-400">{t("vieSociale.noData")}</div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <Kpi i={0} tid="done" icon={CalendarCheck2} label={t("vieSociale.kpi.done")} value={k.done ?? 0} />
              <Kpi i={1} tid="avg" icon={Users} label={t("vieSociale.kpi.avgPart")} value={k.avg_participants ?? 0} suffix={t("vieSociale.suffixHab")} />
              <Kpi i={2} tid="totalPart" icon={TrendingUp} label={t("vieSociale.kpi.totalPart")} value={k.total_participants ?? 0} />
              <Kpi i={3} tid="doneRate" icon={Percent} label={t("vieSociale.kpi.doneRate")} value={k.done_rate ?? 0} suffix=" %" />
              <Kpi i={4} tid="planned" icon={Activity} label={t("vieSociale.kpi.planned")} value={k.planned ?? 0} />
              <Kpi i={5} tid="cancelled" icon={XCircle} label={t("vieSociale.kpi.cancelled")} value={k.cancelled ?? 0} />
              <Kpi i={6} tid="pros" icon={UserCog} label={t("vieSociale.kpi.pros")} value={k.professionals ?? 0} />
              <Kpi i={7} tid="total" icon={CalendarCheck2} label={t("vieSociale.kpi.total")} value={k.total ?? 0} />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
              <Panel title={t("vieSociale.panel.timeline")} className="lg:col-span-2">
                <ResponsiveContainer width="100%" height={260}>
                  <AreaChart data={timeline}>
                    <defs><linearGradient id="g" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={TEAL} stopOpacity={0.5} /><stop offset="100%" stopColor={TEAL} stopOpacity={0} /></linearGradient></defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#ffffff12" />
                    <XAxis dataKey="jour" stroke="#94a3b8" fontSize={12} />
                    <YAxis stroke="#94a3b8" fontSize={12} allowDecimals={false} />
                    <Tooltip contentStyle={{ background: "#0f1e29", border: "1px solid #ffffff20", borderRadius: 12, color: "#fff" }} />
                    <Area type="monotone" dataKey="participants" stroke={TEAL} strokeWidth={2} fill="url(#g)" name={t("vieSociale.series.participants")} />
                  </AreaChart>
                </ResponsiveContainer>
              </Panel>
              <Panel title={t("vieSociale.panel.byCategory")}>
                {activeCats.length ? (
                  <ResponsiveContainer width="100%" height={260}>
                    <PieChart>
                      <Pie data={activeCats} dataKey="value" nameKey="label" innerRadius={55} outerRadius={90} paddingAngle={3}>
                        {activeCats.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
                      </Pie>
                      <Tooltip contentStyle={{ background: "#0f1e29", border: "1px solid #ffffff20", borderRadius: 12, color: "#fff" }} />
                    </PieChart>
                  </ResponsiveContainer>
                ) : <p className="text-slate-500 text-sm py-16 text-center">{t("vieSociale.catToAssign")}</p>}
                <div className="flex flex-wrap gap-2 mt-2">
                  {activeCats.map((c, i) => <span key={c.id} className="text-xs text-slate-300 flex items-center gap-1"><span className="h-2 w-2 rounded-full" style={{ background: PALETTE[i % PALETTE.length] }} />{c.label} ({c.value})</span>)}
                </div>
                {uncCount > 0 && (
                  <button onClick={() => setShowUnc(true)} data-testid="vs-unclassified-btn"
                    className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-lg bg-amber-500/10 hover:bg-amber-500/20 text-amber-300 text-xs font-semibold px-3 py-2 transition ring-1 ring-amber-500/30">
                    <AlertTriangle className="h-3.5 w-3.5" />{t("vieSociale.unclassifiedBtn", { count: uncCount })}
                  </button>
                )}
              </Panel>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
              <Panel title={t("vieSociale.panel.popular")}>
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={data.top_activities} layout="vertical" margin={{ left: 20 }}>
                    <XAxis type="number" stroke="#94a3b8" fontSize={12} allowDecimals={false} />
                    <YAxis type="category" dataKey="label" stroke="#94a3b8" fontSize={12} width={110} />
                    <Tooltip contentStyle={{ background: "#0f1e29", border: "1px solid #ffffff20", borderRadius: 12, color: "#fff" }} />
                    <Bar dataKey="value" fill={TEAL} radius={[0, 6, 6, 0]} name={t("vieSociale.series.done")} />
                  </BarChart>
                </ResponsiveContainer>
              </Panel>
              <Panel title={t("vieSociale.panel.byFunction")}>
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={data.by_function}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#ffffff12" />
                    <XAxis dataKey="label" stroke="#94a3b8" fontSize={11} interval={0} angle={-15} textAnchor="end" height={60} />
                    <YAxis stroke="#94a3b8" fontSize={12} allowDecimals={false} />
                    <Tooltip contentStyle={{ background: "#0f1e29", border: "1px solid #ffffff20", borderRadius: 12, color: "#fff" }} />
                    <Bar dataKey="value" fill="#6366f1" radius={[6, 6, 0, 0]} name={t("vieSociale.series.activities")} />
                  </BarChart>
                </ResponsiveContainer>
              </Panel>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Panel title={t("vieSociale.panel.byDay")}>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={data.by_day}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#ffffff12" />
                    <XAxis dataKey="label" stroke="#94a3b8" fontSize={11} />
                    <YAxis stroke="#94a3b8" fontSize={12} allowDecimals={false} />
                    <Tooltip contentStyle={{ background: "#0f1e29", border: "1px solid #ffffff20", borderRadius: 12, color: "#fff" }} />
                    <Bar dataKey="value" fill="#0ea5e9" radius={[6, 6, 0, 0]} name={t("vieSociale.series.activities")} />
                  </BarChart>
                </ResponsiveContainer>
              </Panel>
              <Panel title={t("vieSociale.panel.byMoment")}>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={data.by_moment}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#ffffff12" />
                    <XAxis dataKey="label" stroke="#94a3b8" fontSize={12} />
                    <YAxis stroke="#94a3b8" fontSize={12} allowDecimals={false} />
                    <Tooltip contentStyle={{ background: "#0f1e29", border: "1px solid #ffffff20", borderRadius: 12, color: "#fff" }} />
                    <Bar dataKey="value" fill="#f59e0b" radius={[6, 6, 0, 0]} name={t("vieSociale.series.activities")} />
                  </BarChart>
                </ResponsiveContainer>
              </Panel>
            </div>
          </>
        )}
        </>
        )}
      </div>

      <Dialog open={showUnc} onOpenChange={setShowUnc}>
        <DialogContent className="bg-[#0f2130] border-white/10 text-slate-100" data-testid="vs-unclassified-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-amber-400" />{t("vieSociale.unclassifiedTitle")}</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-400 -mt-1">{t("vieSociale.unclassifiedDesc")}</p>
          <div className="mt-3 max-h-[50vh] overflow-y-auto space-y-1.5">
            {uncList.length === 0 ? <p className="text-sm text-slate-500 py-4 text-center">{t("vieSociale.unclassifiedNone")}</p> : uncList.map((a, i) => (
              <div key={i} className="flex items-center justify-between rounded-lg bg-white/[0.04] ring-1 ring-white/10 px-3 py-2" data-testid={`vs-unc-item-${i}`}>
                <span className="text-sm text-slate-200">{a.name}</span>
                <span className="text-xs font-semibold text-amber-300">{a.value}×</span>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
